<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import { useDialog } from '@/shared/composables/useDialog';
import SharesTransferForm from './SharesTransferForm.vue';

// Dialog 초기화
const { warning: showWarning } = useDialog();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  taskData?: any;
  taskId?: string;
  showBackButton?: boolean;
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 완료 상태)
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: '',
  showBackButton: true,
  readOnly: false
});

// Emits
const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [error: any];
  'validation-success': [data: { eFilingData: any; entityUEN: string; filingAgentUEN: string; entityName: string; apiName?: string }];
}>();

// SharesTransferForm ref
const formRef = ref<InstanceType<typeof SharesTransferForm> | null>(null);

// 상태
const isSubmitting = ref(false);
const isValidating = ref(false);

// 검증 결과
const validationResult = ref<any>(null);

// 필드명을 사용자 친화적으로 변환
const formatFieldLabel = (field: any): string => {
  let label = field.label || field.field || '';
  if (label.endsWith(' Code') || label.endsWith(' Name')) {
    label = label.replace(/ Code$/, '').replace(/ Name$/, '');
  }
  return label;
};

// 필드별 validation 에러 매핑 (SharesTransferForm에 전달)
const fieldValidationErrors = computed(() => {
  const fields = validationResult.value?.data?.missingFields || [];
  if (!fields.length) return {};

  const errors: Record<string, string> = {};
  
  fields.forEach((field: any) => {
    const fieldPath = field.field || '';
    const label = formatFieldLabel(field);
    
    const mappedField = mapFieldPathToComponentField(fieldPath);
    if (mappedField) {
      errors[mappedField] = t('startTask.This field is required') || `${label} is required`;
    }
  });

  return errors;
});

// 백엔드 필드 경로를 컴포넌트 필드명으로 매핑
const mapFieldPathToComponentField = (fieldPath: string): string | null => {
  const fieldMap: Record<string, string> = {
    'transactionDate': 'transactionDate',
    'numberOfShares': 'toNumberOfShares',
    'fromNumberOfShares': 'fromNumberOfShares',
    'transferFrom': 'fromShareholder',
    'transferFromName': 'fromShareholderName',
    'transferTo': 'toShareholder',
    'transferToName': 'toShareholderName',
    'fromCategory.code': 'fromCategory',
    'fromCategory.name': 'fromCategory',
    'fromCategoryType': 'fromCategoryType',
    'fromIdType.code': 'fromIdType',
    'fromIdType.name': 'fromIdType',
    'fromNationality.code': 'fromNationality',
    'fromNationality.name': 'fromNationality',
    'fromUen': 'fromUen',
    'category.code': 'category',
    'category.name': 'category',
    'categoryType': 'categoryType',
    'idType.code': 'idType',
    'idType.name': 'idType',
    'nationality.code': 'nationality',
    'nationality.name': 'nationality',
    'toUen': 'toUen',
    'contactEmail': 'contactEmail',
    'currency.code': 'currency',
    'currency.name': 'currency',
    'classOfShares.code': 'classOfShares',
    'classOfShares.name': 'classOfShares',
    'entityUEN': 'entityUEN',
    'filingAgentUEN': 'filingAgentUEN',
  };

  if (fieldMap[fieldPath]) {
    return fieldMap[fieldPath];
  }

  const pathParts = fieldPath.split('.');
  if (pathParts.length > 1) {
    const baseField = pathParts[0];
    if (fieldMap[fieldPath]) {
      return fieldMap[fieldPath];
    }
    if (fieldMap[baseField]) {
      return fieldMap[baseField];
    }
  }

  return null;
};

// 최종 전송용 데이터 (eFilingDataForApi)
const finalEFilingData = ref<any>(null);

// Entity 정보 (taskData 또는 finalEFilingData에서 추출)
const entityUEN = computed(() => {
  const fromEFilingDataForApi = props.taskData?.eFilingDataForApi?.EntityUEN;
  const fromEFilingDataValidation = props.taskData?.eFilingDataValidation?.EntityUEN;
  const fromTaskData = props.taskData?.entityUEN;
  const fromFinalData = finalEFilingData.value?.EntityUEN;
  const fromShareholderList = props.taskData?.eFilingDataForApi?.ShareholderList?.[0]?.EntityUEN ||
                              props.taskData?.eFilingDataValidation?.ShareholderList?.[0]?.EntityUEN ||
                              finalEFilingData.value?.ShareholderList?.[0]?.EntityUEN;

  const result = fromEFilingDataForApi || fromEFilingDataValidation || fromTaskData || fromFinalData || fromShareholderList || '';
  return result;
});

const filingAgentUEN = computed(() => {
  const fromEFilingDataForApi = props.taskData?.eFilingDataForApi?.FilingAgentUEN;
  const fromEFilingDataValidation = props.taskData?.eFilingDataValidation?.FilingAgentUEN;
  const fromTaskData = props.taskData?.filingAgentUEN;
  const fromFinalData = finalEFilingData.value?.FilingAgentUEN;

  return fromEFilingDataForApi || fromEFilingDataValidation || fromTaskData || fromFinalData || entityUEN.value;
});

const entityName = computed(() => {
  return props.taskData?.eFilingData?.entityName?.name ||
         props.taskData?.eFilingData?.entityName ||
         props.taskData?.eFilingDataValidation?.entityName?.name ||
         props.taskData?.eFilingDataValidation?.entityName ||
         props.taskData?.eFilingDataForApi?.entityName ||
         props.taskData?.entityName ||
         finalEFilingData.value?.entityName ||
         '';
});

// eFilingDataForApi의 ShareholderList를 formData.shareholderTransferSections 구조로 변환
const initialData = computed(() => {
  if (!props.taskData) return null;
  
  const formData = props.taskData.formData || {};
  const shareholderListForApi = props.taskData.eFilingDataForApi?.ShareholderList || [];
  
  // 값 추출 헬퍼 함수 (객체인 경우 value 속성 사용)
  const getValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && 'value' in val) return String(val.value || '');
    return String(val);
  };
  
  // transactionDate 값 추출 (객체일 수 있음)
  const transactionDateValue = getValue(formData.transactionDate);
  
  // eFilingDataForApi가 있으면 그것을 우선 사용, 없으면 formData 사용
  // formData.shareholderTransferSections가 있으면 eFilingDataForApi의 값으로 업데이트
  if (formData.shareholderTransferSections && Array.isArray(formData.shareholderTransferSections)) {
    const updatedSections = formData.shareholderTransferSections.map((section: any, index: number) => {
      // Deep copy로 새 객체 생성 (중첩된 객체 참조 문제 해결)
      const updatedSection = JSON.parse(JSON.stringify(section));
      
      // eFilingDataForApi가 있고 해당 인덱스의 항목이 있으면 그것을 우선 사용
      if (shareholderListForApi.length > 0 && shareholderListForApi[index]) {
        const transferForApi = shareholderListForApi[index];
        
        // eFilingDataForApi의 값이 있으면 우선 사용 (빈 문자열이 아닌 경우)
        // 빈 문자열이면 formData의 값을 유지
        if (transferForApi.toNumberOfShares !== undefined && 
            transferForApi.toNumberOfShares !== null && 
            String(transferForApi.toNumberOfShares).trim() !== '') {
          updatedSection.toNumberOfShares = String(transferForApi.toNumberOfShares);
        }
        if (transferForApi.toIssuedShareCapital !== undefined && 
            transferForApi.toIssuedShareCapital !== null && 
            String(transferForApi.toIssuedShareCapital).trim() !== '') {
          updatedSection.toIssuedShareCapital = String(transferForApi.toIssuedShareCapital);
        } else if (transferForApi.issuedShareCapital !== undefined &&
            transferForApi.issuedShareCapital !== null &&
            String(transferForApi.issuedShareCapital).trim() !== '') {
          // PreSet seed uses issuedShareCapital (not toIssuedShareCapital)
          updatedSection.toIssuedShareCapital = String(transferForApi.issuedShareCapital);
        }
        if (transferForApi.toPaidUpShareCapital !== undefined && 
            transferForApi.toPaidUpShareCapital !== null && 
            String(transferForApi.toPaidUpShareCapital).trim() !== '') {
          updatedSection.toPaidUpShareCapital = String(transferForApi.toPaidUpShareCapital);
        } else if (transferForApi.paidUpShareCapital !== undefined &&
            transferForApi.paidUpShareCapital !== null &&
            String(transferForApi.paidUpShareCapital).trim() !== '') {
          updatedSection.toPaidUpShareCapital = String(transferForApi.paidUpShareCapital);
        }
        if (transferForApi.fromNumberOfShares !== undefined && 
            transferForApi.fromNumberOfShares !== null && 
            String(transferForApi.fromNumberOfShares).trim() !== '') {
          updatedSection.fromNumberOfShares = String(transferForApi.fromNumberOfShares);
        }
        if (transferForApi.fromPaidUpShareCapital !== undefined && 
            transferForApi.fromPaidUpShareCapital !== null && 
            String(transferForApi.fromPaidUpShareCapital).trim() !== '') {
          updatedSection.fromPaidUpShareCapital = String(transferForApi.fromPaidUpShareCapital);
        }
        if (transferForApi.totalConsiderationPaid !== undefined && 
            transferForApi.totalConsiderationPaid !== null && 
            String(transferForApi.totalConsiderationPaid).trim() !== '') {
          updatedSection.totalConsiderationPaid = String(transferForApi.totalConsiderationPaid);
        } else if (transferForApi.considerationPaid !== undefined &&
            transferForApi.considerationPaid !== null &&
            String(transferForApi.considerationPaid).trim() !== '') {
          updatedSection.totalConsiderationPaid = String(transferForApi.considerationPaid);
        }
        // PreSet: Issued / Paid up capital empty → fill from Consideration Paid
        const considerationFallback = String(updatedSection.totalConsiderationPaid || '').trim();
        if (considerationFallback) {
          if (!String(updatedSection.toIssuedShareCapital || '').trim()) {
            updatedSection.toIssuedShareCapital = considerationFallback;
          }
          if (!String(updatedSection.toPaidUpShareCapital || '').trim()) {
            updatedSection.toPaidUpShareCapital = considerationFallback;
          }
        }
        // Certificate Details (PreSet / API seed)
        if (transferForApi.certiNoFrom !== undefined &&
            transferForApi.certiNoFrom !== null &&
            String(transferForApi.certiNoFrom).trim() !== '') {
          updatedSection.fromShareholderCertNo = String(transferForApi.certiNoFrom);
        }
        if (transferForApi.certiTypeFrom !== undefined &&
            transferForApi.certiTypeFrom !== null &&
            String(transferForApi.certiTypeFrom).trim() !== '') {
          updatedSection.fromShareholderCertType = String(transferForApi.certiTypeFrom);
        }
        if (transferForApi.certiNoTo !== undefined &&
            transferForApi.certiNoTo !== null &&
            String(transferForApi.certiNoTo).trim() !== '') {
          updatedSection.toShareholderCertNo = String(transferForApi.certiNoTo);
        }
        if (transferForApi.certiTypeTo !== undefined &&
            transferForApi.certiTypeTo !== null &&
            String(transferForApi.certiTypeTo).trim() !== '') {
          updatedSection.toShareholderCertType = String(transferForApi.certiTypeTo);
        }
        // shareType 업데이트 (formData.shareType이 코드값이므로 우선 사용)
        // formData.shareType이 이미 코드값이므로, eFilingDataForApi의 classOfShares(이름 값)보다 우선
        // classOfSharesCd만 사용 (코드값)
        if (!updatedSection.shareType || String(updatedSection.shareType).trim() === '') {
          if (transferForApi.classOfSharesCd !== undefined && 
              transferForApi.classOfSharesCd !== null && 
              String(transferForApi.classOfSharesCd).trim() !== '') {
            updatedSection.shareType = String(transferForApi.classOfSharesCd);
          }
        }
        // formData.shareType이 있으면 그대로 사용 (코드값)
        // currency 업데이트 (currency 또는 currencyCd)
        if (transferForApi.currency !== undefined && 
            transferForApi.currency !== null && 
            String(transferForApi.currency).trim() !== '') {
          updatedSection.currency = String(transferForApi.currency);
        } else if (transferForApi.currencyCd !== undefined && 
                   transferForApi.currencyCd !== null && 
                   String(transferForApi.currencyCd).trim() !== '') {
          updatedSection.currency = String(transferForApi.currencyCd);
        }
        // enteredMember 업데이트 (effectiveDate)
        if (transferForApi.effectiveDate !== undefined && 
            transferForApi.effectiveDate !== null && 
            String(transferForApi.effectiveDate).trim() !== '') {
          updatedSection.enteredMember = String(transferForApi.effectiveDate);
        }
      } else {
        // eFilingDataForApi가 없으면 formData의 값을 사용하되, 객체인 경우 value 추출
        updatedSection.toNumberOfShares = getValue(updatedSection.toNumberOfShares);
        updatedSection.toIssuedShareCapital = getValue(updatedSection.toIssuedShareCapital);
        updatedSection.toPaidUpShareCapital = getValue(updatedSection.toPaidUpShareCapital);
        updatedSection.fromNumberOfShares = getValue(updatedSection.fromNumberOfShares);
        updatedSection.fromPaidUpShareCapital = getValue(updatedSection.fromPaidUpShareCapital);
        updatedSection.totalConsiderationPaid = getValue(updatedSection.totalConsiderationPaid);
        updatedSection.shareType = getValue(updatedSection.shareType);
        updatedSection.currency = getValue(updatedSection.currency);
        updatedSection.enteredMember = getValue(updatedSection.enteredMember);
        const considerationFallback = String(updatedSection.totalConsiderationPaid || '').trim();
        if (considerationFallback) {
          if (!String(updatedSection.toIssuedShareCapital || '').trim()) {
            updatedSection.toIssuedShareCapital = considerationFallback;
          }
          if (!String(updatedSection.toPaidUpShareCapital || '').trim()) {
            updatedSection.toPaidUpShareCapital = considerationFallback;
          }
        }
      }
      
      return updatedSection;
    });
    
    return {
      formData: {
        ...formData, // entity는 여기서 그대로 전달됨 (Allotment와 동일)
        transactionDate: shareholderListForApi.length > 0 && shareholderListForApi[0]?.transactionDate
          ? shareholderListForApi[0].transactionDate
          : transactionDateValue,
        shareholderTransferSections: updatedSections
      }
    };
  }
  
  // formData.shareholders가 있으면 shareholderTransferSections로 변환 (Allotment와 동일한 패턴)
  // PreSet Transfer: shareholders에 toIssued/toPaidUp 없음 → API seed 또는 Consideration Paid로 채움
  if (formData.shareholders && Array.isArray(formData.shareholders)) {
    const sections = formData.shareholders.map((shareholder: any, index: number) => {
      const fromApi = shareholderListForApi[index] || {};
      const consideration =
        getValue(shareholder.totalConsiderationPaid) ||
        getValue(fromApi.considerationPaid) ||
        getValue(fromApi.totalConsiderationPaid);
      const toIssued =
        getValue(shareholder.toIssuedShareCapital) ||
        getValue(fromApi.issuedShareCapital) ||
        getValue(fromApi.toIssuedShareCapital) ||
        consideration;
      const toPaidUp =
        getValue(shareholder.toPaidUpShareCapital) ||
        getValue(fromApi.paidUpShareCapital) ||
        getValue(fromApi.toPaidUpShareCapital) ||
        consideration;
      return {
        id: String(index + 1),
        fromShareholder: shareholder.fromShareholder || '',
        fromNumberOfShares: getValue(shareholder.fromNumberOfShares),
        fromPaidUpShareCapital:
          getValue(shareholder.fromPaidUpShareCapital) ||
          getValue(fromApi.fromPaidUpShareCapital) ||
          consideration,
        fromShareholderID: getValue(shareholder.fromShareholderID),
        fromShareholderAddress: getValue(shareholder.fromShareholderAddress),
        toShareholder: shareholder.toShareholder || '',
        toNumberOfShares:
          getValue(shareholder.toNumberOfShares) ||
          getValue(fromApi.numberOfShares) ||
          getValue(fromApi.toNumberOfShares),
        toIssuedShareCapital: toIssued,
        toPaidUpShareCapital: toPaidUp,
        totalConsiderationPaid: consideration,
        toShareholderID: getValue(shareholder.toShareholderID),
        toShareholderAddress: getValue(shareholder.toShareholderAddress),
        toShareholderNationality: getValue(shareholder.toShareholderNationality),
        toShareholderBirth: getValue(shareholder.toShareholderBirth),
        fromShareholderCertType:
          getValue(shareholder.fromShareholderCertType) ||
          getValue(fromApi.certiTypeFrom) ||
          '',
        fromShareholderCertNo:
          getValue(shareholder.fromShareholderCertNo) ||
          getValue(shareholder.fromShareCertificateNo) ||
          getValue(fromApi.certiNoFrom) ||
          '',
        toShareholderCertType:
          getValue(shareholder.toShareholderCertType) ||
          getValue(fromApi.certiTypeTo) ||
          '',
        toShareholderCertNo:
          getValue(shareholder.toShareholderCertNo) ||
          getValue(shareholder.toShareCertificateNo) ||
          getValue(fromApi.certiNoTo) ||
          '',
        shareType: getValue(shareholder.shareType),
        currency: getValue(shareholder.currency),
        showTrustSection: false,
        sharesHeldInTrust: shareholder.sharesHeldInTrust || null,
        trustName: shareholder.trustName || '',
        hasSubClass: false,
        subClassErrorMessage: '',
        toNumberOfSharesError: '',
        enteredMember: ''
      };
    });
    
    const first = sections[0];
    return {
      formData: {
        ...formData, // entity는 여기서 그대로 전달됨 (Allotment와 동일)
        transactionDate: transactionDateValue,
        shareholderTransferSections: sections,
        // Certificate Detail top-level fallback (섹션 0)
        transferorCertificateType: first?.fromShareholderCertType || '',
        fromShareCertificateNo: first?.fromShareholderCertNo || '',
        transferorCertificateNo: first?.fromShareholderCertNo || '',
        transfereeCertificateType: first?.toShareholderCertType || '',
        toShareCertificateNo: first?.toShareholderCertNo || '',
        transfereeCertificateNo: first?.toShareholderCertNo || '',
      }
    };
  }
  
  // formData.shareholderTransferSections가 없으면 eFilingDataForApi에서 생성
  if (shareholderListForApi.length > 0) {
    const sections = shareholderListForApi.map((transfer: any) => {
      const consideration = transfer.considerationPaid || transfer.totalConsiderationPaid || '';
      return {
        fromShareholder: transfer.transferFrom || transfer.contactCd || '',
        fromNumberOfShares: transfer.fromNumberOfShares || '',
        fromPaidUpShareCapital: transfer.fromPaidUpShareCapital || consideration || '',
        toShareholder: transfer.transferTo || '',
        toNumberOfShares: transfer.numberOfShares || transfer.toNumberOfShares || '',
        toIssuedShareCapital: transfer.issuedShareCapital || transfer.toIssuedShareCapital || consideration || '',
        toPaidUpShareCapital: transfer.paidUpShareCapital || transfer.toPaidUpShareCapital || consideration || '',
        totalConsiderationPaid: consideration,
        fromShareholderCertType: transfer.certiTypeFrom || '',
        fromShareholderCertNo: transfer.certiNoFrom || '',
        toShareholderCertType: transfer.certiTypeTo || '',
        toShareholderCertNo: transfer.certiNoTo || '',
        shareType: transfer.classOfShares || transfer.classOfSharesCd || '',
        currency: transfer.currency || transfer.currencyCd || '',
        showTrustSection: false,
        sharesHeldInTrust: null,
        trustName: '',
        hasSubClass: false,
        subClassErrorMessage: '',
        toNumberOfSharesError: '',
        enteredMember: ''
      };
    });
    
    const first = sections[0];
    return {
      formData: {
        ...formData, // entity는 여기서 그대로 전달됨 (Allotment와 동일)
        transactionDate: shareholderListForApi[0]?.transactionDate || transactionDateValue,
        shareholderTransferSections: sections,
        transferorCertificateType: first?.fromShareholderCertType || '',
        fromShareCertificateNo: first?.fromShareholderCertNo || '',
        transferorCertificateNo: first?.fromShareholderCertNo || '',
        transfereeCertificateType: first?.toShareholderCertType || '',
        toShareCertificateNo: first?.toShareholderCertNo || '',
        transfereeCertificateNo: first?.toShareholderCertNo || '',
      }
    };
  }
  
  // Allotment와 동일하게 단순화
  return { formData };
});

// 뒤로가기
const handleBack = () => {
  emit('back');
};

// Deep merge 함수 - 빈 값은 기존 값 유지
const deepMerge = (base: any, update: any): any => {
  if (!base) return update;
  if (!update) return base;

  if (typeof base !== 'object' || typeof update !== 'object') {
    if (update === '' || update === null || update === undefined) {
      return base;
    }
    return update;
  }

  if (Array.isArray(base) && Array.isArray(update)) {
    const maxLen = Math.max(base.length, update.length);
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      if (i < update.length && i < base.length) {
        result.push(deepMerge(base[i], update[i]));
      } else if (i < base.length) {
        result.push(base[i]);
      } else {
        result.push(update[i]);
      }
    }
    return result;
  }

  const result = { ...base };
  for (const key of Object.keys(update)) {
    const updateValue = update[key];
    const baseValue = base[key];

    if (typeof updateValue === 'object' && updateValue !== null &&
        'code' in updateValue && 'name' in updateValue) {
      if (!updateValue.code && !updateValue.name) {
        result[key] = baseValue || updateValue;
      } else if (!updateValue.code && baseValue?.code) {
        result[key] = { ...updateValue, code: baseValue.code };
      } else if (!updateValue.name && baseValue?.name) {
        result[key] = { ...updateValue, name: baseValue.name };
      } else {
        result[key] = deepMerge(baseValue, updateValue);
      }
    } else {
      result[key] = deepMerge(baseValue, updateValue);
    }
  }

  return result;
};

// task_data 기반으로 eFilingData 업데이트 (deep merge)
const mergeTaskDataWithEFiling = (baseTaskData: any, newEFilingData: { eFilingDataValidation: any; eFilingDataForApi: any }): any => {
  const merged = JSON.parse(JSON.stringify(baseTaskData || {}));

  if (merged.eFilingDataValidation) {
    merged.eFilingDataValidation = deepMerge(merged.eFilingDataValidation, newEFilingData.eFilingDataValidation);
  } else {
    merged.eFilingDataValidation = newEFilingData.eFilingDataValidation;
  }

  if (merged.eFilingDataForApi) {
    merged.eFilingDataForApi = deepMerge(merged.eFilingDataForApi, newEFilingData.eFilingDataForApi);
  } else {
    merged.eFilingDataForApi = newEFilingData.eFilingDataForApi;
  }

  return merged;
};

// 디버깅용 데이터
const debugData = ref<{
  originalEFilingData: any;
  mergedEFilingData: any;
  mergedEFilingDataForApi: any;
  changes: Array<{ path: string; oldValue: any; newValue: any }>;
}>({
  originalEFilingData: null,
  mergedEFilingData: null,
  mergedEFilingDataForApi: null,
  changes: []
});

// Submit - E-Filing 데이터 생성 후 검증 및 저장
const handleSubmit = async () => {
  console.log('📋 [SharesTransferEFiling] Submit 클릭');

  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!formRef.value) {
      console.error('❌ [SharesTransferEFiling] Form ref가 없습니다');
      return;
    }

    // SharesTransferForm의 getEFilingData 호출
    const updatedEFilingData = await formRef.value.getEFilingData?.();
    console.log('📋 [SharesTransferEFiling] Updated E-Filing 데이터:', updatedEFilingData);

    // task_data 기반으로 eFilingData 업데이트
    const mergedData = mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    console.log('📋 [SharesTransferEFiling] Merged 데이터:', mergedData);

    const originalEFilingDataValidation = props.taskData?.eFilingDataValidation || null;
    const mergedEFilingDataValidation = mergedData.eFilingDataValidation || null;
    const mergedEFilingDataForApi = mergedData.eFilingDataForApi || null;

    debugData.value = {
      originalEFilingData: originalEFilingDataValidation,
      mergedEFilingData: mergedEFilingDataValidation,
      mergedEFilingDataForApi: mergedEFilingDataForApi,
      changes: []
    };

    // 바로 검증 및 저장 실행
    await handleValidateAndSubmit();
  } finally {
    isSubmitting.value = false;
  }
};

// 디버깅 다이얼로그에서 OK 클릭 - eFilingData 저장 후 /validate/:taskId 호출
const handleValidateAndSubmit = async () => {
  console.log('📋 [SharesTransferEFiling] Save & Validate 호출');

  if (isValidating.value) return;

  try {
    isValidating.value = true;

    if (!props.taskId) {
      console.error('❌ [SharesTransferEFiling] taskId가 없습니다');
      validationResult.value = {
        success: false,
        error: 'Task ID is required'
      };
      return;
    }

    // 1. eFilingDataValidation + eFilingDataForApi 저장
    console.log('📤 [SharesTransferEFiling] eFilingData 저장 중...');
    const saveResponse = await apiClient.put(
      `/startTask/shares-transfer/update-efiling-data/${props.taskId}`,
      {
        eFilingDataValidation: debugData.value.mergedEFilingData,
        eFilingDataForApi: debugData.value.mergedEFilingDataForApi
      }
    );
    console.log('✅ [SharesTransferEFiling] eFilingData 저장 완료:', saveResponse);

    // 2. /validate/:taskId API 호출
    console.log('📤 [SharesTransferEFiling] Validate 호출 중...');
    const response = await apiClient.get(`/startTask/shares-transfer/validate/${props.taskId}`);
    console.log('✅ [SharesTransferEFiling] Validate 응답:', response);

    validationResult.value = {
      success: true,
      data: response?.data || response
    };

    // 검증 성공 시 최종 전송용 데이터 사용
    if (validationResult.value?.data?.isValid) {
      finalEFilingData.value = debugData.value.mergedEFilingDataForApi;
      console.log('📋 [SharesTransferEFiling] 최종 API용 데이터:', finalEFilingData.value);

      // To Shareholder의 categoryType에 따라 apiName 결정
      const toCategoryType = finalEFilingData.value?.ShareholderList?.[0]?.categoryType;
      let apiName = 'SaveDraftTransferShareholderIND'; // 기본값
      if (toCategoryType === 'LC' || toCategoryType === 'FC') {
        apiName = 'SaveDraftTransferShareholderCORP';
      }

      // 검증 성공 시 부모 컴포넌트(EFilingPage)로 데이터 전달하여 Singpass 모달 호출
      emit('validation-success', {
        eFilingData: finalEFilingData.value,
        entityUEN: entityUEN.value,
        filingAgentUEN: filingAgentUEN.value,
        entityName: entityName.value,
        apiName: apiName
      });
    } else {
      // 검증 실패 시 알림창 표시
      await showWarning(
        t('startTask.Validation Error') || 'Validation Error',
        t('startTask.Please check the required fields and enter the values') || 'Please check the required fields and enter the values.'
      );
    }
  } catch (error: any) {
    console.error('❌ [SharesTransferEFiling] Save & Validate 실패:', error);
    validationResult.value = {
      success: false,
      error: error.message || 'Save or Validation failed'
    };
  } finally {
    isValidating.value = false;
  }
};

// Form ref를 외부에서 접근 가능하도록 expose
defineExpose({
  formRef,
  getEFilingData: async () => {
    if (formRef.value) {
      return await formRef.value.getEFilingData?.();
    }
    return null;
  },
  getMergedData: async () => {
    if (formRef.value) {
      const updatedEFilingData = await formRef.value.getEFilingData?.();
      return mergeTaskDataWithEFiling(props.taskData, updatedEFilingData);
    }
    return null;
  }
});
</script>

<template>
  <v-card rounded="md" class="shares-transfer-efiling">
    <v-card-text>
      <SharesTransferForm
        ref="formRef"
        :initial-data="initialData"
        :read-only="readOnly"
        :show-task-flow="false"
        :e-filing-mode="true"
        :validation-errors="fieldValidationErrors"
      />
    </v-card-text>
    <v-divider v-if="!readOnly" />
    <v-card-actions v-if="!readOnly" class="justify-end pa-4 pr-8">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="primary"
        :disabled="isSubmitting || isValidating"
        @click="handleBack"
      >
        {{ t('startTask.Back') || 'Back' }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :loading="isSubmitting || isValidating"
        :disabled="isSubmitting || isValidating"
        @click="handleSubmit"
      >
        {{ t('startTask.Submit') || 'Submit' }}
      </v-btn>
    </v-card-actions>
    <!-- 읽기 전용 모드일 때 완료 상태 표시 -->
    <v-divider v-if="readOnly" />
    <v-card-actions v-if="readOnly" class="justify-center pa-4">
      <v-alert type="success" variant="tonal" density="compact" class="mb-0">
        {{ t('startTask.E-Filing has been completed') || 'E-Filing has been completed' }}
      </v-alert>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.shares-transfer-efiling {
  margin: 16px;
  max-width: 100%;
  box-sizing: border-box;
}

.shares-transfer-efiling :deep(.v-card-text) {
  max-width: 100%;
  box-sizing: border-box;
}

.shares-transfer-efiling :deep(.v-row) {
  margin-left: 0;
  margin-right: 0;
}

.shares-transfer-efiling :deep(.v-col) {
  padding-left: 8px;
  padding-right: 8px;
}

.shares-transfer-efiling :deep(.v-card-actions) {
  padding-right: 32px !important;
}
</style>
